import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { TicketPriority } from "@/lib/types";
import { AlertCircle, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";

const priorityBadgeVariants = cva(
  "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full",
  {
    variants: {
      priority: {
        low: "bg-emerald-100 text-emerald-700 border border-emerald-200",
        medium: "bg-amber-100 text-amber-700 border border-amber-200",
        high: "bg-orange-100 text-orange-700 border border-orange-200",
        critical: "bg-red-100 text-red-700 border border-red-200",
      },
    },
    defaultVariants: {
      priority: "medium",
    },
  },
);

interface PriorityBadgeProps extends VariantProps<
  typeof priorityBadgeVariants
> {
  priority: TicketPriority;
  className?: string;
  showIcon?: boolean;
}

const priorityIcons: Record<TicketPriority, React.ReactNode> = {
  low: <ArrowDown className="h-3 w-3" />,
  medium: <ArrowUp className="h-3 w-3" />,
  high: <AlertTriangle className="h-3 w-3" />,
  critical: <AlertCircle className="h-3 w-3" />,
};

const priorityLabels: Record<TicketPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export function PriorityBadge({
  priority,
  className,
  showIcon = true,
}: PriorityBadgeProps) {
  return (
    <span className={cn(priorityBadgeVariants({ priority }), className)}>
      {showIcon && priorityIcons[priority]}
      {priorityLabels[priority]}
    </span>
  );
}
